import type { VisionRunCounts, VisionRunView } from "@sitescene/types";

import { VisionCountMode, VisionRunStatus } from "@sitescene/types";

import { DetailPanel } from "@/components/detail-panel";
import { classColor, formatSeconds } from "@/features/vision-lab/format";
import { cx } from "@/utils/cx";

const STEPS: { label: string; status: VisionRunStatus }[] = [
	{ label: "Waiting for the GPU", status: VisionRunStatus.WaitingForGpu },
	{ label: "Detecting", status: VisionRunStatus.Detecting },
	{ label: "Describing", status: VisionRunStatus.Describing },
	{ label: "Answering", status: VisionRunStatus.Answering },
];

interface RunPanelProps {
	/** Jump to an object: when Gemini saw it, else its first sighting. */
	onSelect: (objectId: number, seconds: number) => void;
	run: VisionRunView;
	selectedId?: number;
}

/** A run's progress, then its answer, count, objects and cost. */
export const RunPanel = ({ onSelect, run, selectedId }: RunPanelProps) => {
	if (run.status === VisionRunStatus.Failed) {
		return (
			<DetailPanel title="Run failed">
				<p className="text-sm text-error-primary" role="alert">
					{run.error ?? "The run failed."}
				</p>
			</DetailPanel>
		);
	}

	if (run.status !== VisionRunStatus.Completed) {
		const current = STEPS.findIndex((step) => step.status === run.status);
		return (
			<DetailPanel title="Running">
				<ol className="flex flex-col gap-2 text-sm">
					{STEPS.map((step, index) => (
						<li className="flex items-center gap-2" key={step.status}>
							<span
								aria-hidden="true"
								className={cx(
									"size-2.5 rounded-full",
									index < current && "bg-success-500",
									index === current && "animate-pulse bg-brand-500",
									index > current && "bg-gray-300",
								)}
							/>
							<span
								className={
									index === current
										? "font-medium text-primary"
										: "text-tertiary"
								}
							>
								{step.label}
								{index === current &&
									step.status === VisionRunStatus.WaitingForGpu &&
									" (about 5 minutes if the GPU was off)"}
							</span>
						</li>
					))}
				</ol>
			</DetailPanel>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			<DetailPanel title="Answer">
				<p className="text-sm whitespace-pre-line text-primary">{run.answer}</p>
				{run.counts && <CountSummary counts={run.counts} />}
				<p className="mt-3 text-xs text-tertiary">
					GPU {run.gpuSeconds ?? "—"} s · Gemini about $
					{(run.geminiCostUsd ?? 0).toFixed(3)}
				</p>
			</DetailPanel>

			<DetailPanel title={`Objects (${run.objects.length})`}>
				{run.objects.length === 0 ? (
					<p className="text-sm text-tertiary">
						RF-DETR tracked nothing it was asked for.
					</p>
				) : (
					<ul className="flex max-h-[28rem] flex-col gap-1 overflow-y-auto">
						{run.objects.map((object) => (
							<li key={object.id}>
								<button
									className={cx(
										"flex w-full items-center gap-3 rounded px-2 py-1 text-left text-sm hover:bg-brand-50",
										selectedId === object.id && "bg-brand-50",
									)}
									// To the moment Gemini saw it, when it said
									onClick={() =>
										onSelect(object.id, object.seenAt ?? object.firstT)
									}
									type="button"
								>
									{run.cropUrls[String(object.id)] ? (
										<img
											alt=""
											className="h-12 w-9 shrink-0 rounded object-cover"
											src={run.cropUrls[String(object.id)]}
										/>
									) : (
										<span className="h-12 w-9 shrink-0 rounded bg-gray-200" />
									)}
									<span className="flex flex-col">
										<span
											className="font-medium"
											style={{ color: classColor(object.class) }}
										>
											#{object.id} {object.class}
											{object.counted && (
												<span className="ml-1 text-xs text-tertiary">
													counted
												</span>
											)}
										</span>
										{object.description && (
											<span className="text-secondary">
												{object.description}
												{object.seenAt !== undefined && (
													<span className="text-tertiary">
														{" "}
														at {formatSeconds(object.seenAt)}
													</span>
												)}
											</span>
										)}
										<span className="text-xs text-tertiary">
											{formatSeconds(object.firstT)}–
											{formatSeconds(object.lastT)}
										</span>
									</span>
								</button>
							</li>
						))}
					</ul>
				)}
			</DetailPanel>
		</div>
	);
};

const CountSummary = ({ counts }: { counts: VisionRunCounts }) => {
	const byClass = Object.entries(counts.byClass)
		.map(([name, count]) => `${count} ${name}`)
		.join(", ");
	const headline =
		counts.mode === VisionCountMode.Line
			? `${counts.total ?? 0} crossed: ${counts.in ?? 0} in, ${counts.out ?? 0} out`
			: counts.mode === VisionCountMode.Area
				? `${counts.present ?? 0} inside the area, ${counts.entered ?? 0} walked in`
				: `${counts.total ?? 0} unique tracks`;
	return (
		<p className="mt-2 text-sm text-secondary">
			<span className="font-medium text-primary">{headline}</span>
			{byClass && ` (${byClass})`}
		</p>
	);
};
